import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './ReviewHeroUpload.module.sass';
import { getAuthToken } from '../../utils/authHelpers';

/**
 * ReviewHeroUpload — "Photo from your collab" field on the Collab Tracker
 * review form. Uploads to Cloudinary via the signed-upload endpoint
 * (kind: 'review_hero' → postaway/review-heroes folder), mirroring the
 * ShowcaseUpload pattern.
 *
 * Controlled via the `hero` object owned by the parent:
 *   { url, localUrl, uploading, progress, error }
 * - localUrl (object URL) is set immediately so the live share-card preview
 *   updates before the upload finishes; url swaps in when Cloudinary is done.
 * - The photo is optional and must NEVER block a review: errors are inline
 *   and the parent keeps Submit enabled throughout.
 */

export const EMPTY_HERO = { url: null, localUrl: null, uploading: false, progress: 0, error: null };

const MAX_IMAGE_SIZE = 15 * 1024 * 1024;
const MAX_DIMENSION = 2400;
const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

export const isHeicFile = (file) => {
  const ext = String(file?.name || '').split('.').pop().toLowerCase();
  const mime = (file?.type || '').toLowerCase();
  return ext === 'heic' || ext === 'heif' || mime === 'image/heic' || mime === 'image/heif';
};

export const validateFile = (file) => {
  if (!file) return 'No file selected.';
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  const mime = (file.type || '').toLowerCase();
  if (!ALLOWED_EXT.includes(ext) && !mime.startsWith('image/')) {
    return 'Please upload a JPG, PNG, or HEIC photo.';
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return 'Photos must be 15MB or smaller.';
  }
  return null;
};

/**
 * Client-side downscale to MAX_DIMENSION before upload. HEIC can't be decoded
 * by canvas in most browsers, so those upload as-is — Cloudinary's eager
 * c_limit,w_2400,f_jpg transform does the downscale + conversion server-side.
 */
export async function downscaleImage(file) {
  if (isHeicFile(file)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const largest = Math.max(width, height);
    if (largest <= MAX_DIMENSION) {
      bitmap.close?.();
      return file;
    }
    const scale = MAX_DIMENSION / largest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) return file;
    const baseName = String(file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch (_) {
    // Downscale is an optimization, never a gate.
    return file;
  }
}

export const requestSignature = async () => {
  const token = getAuthToken();
  const res = await fetch('/api/cloudinary/sign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ kind: 'review_hero' }),
  });
  const data = await res.json();
  if (!res.ok || !data.ok) {
    throw new Error(data.error === 'unauthorized' ? 'Please sign in before uploading.' : 'Could not prepare upload.');
  }
  return data;
};

export const uploadToCloudinary = (file, signatureData, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', signatureData.apiKey);
    formData.append('timestamp', signatureData.timestamp);
    formData.append('signature', signatureData.signature);
    formData.append('public_id', signatureData.publicId);
    formData.append('eager', signatureData.eager);
    formData.append('overwrite', 'true');
    formData.append('invalidate', 'true');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.error?.message || 'Upload failed.'));
      } catch (error) {
        reject(error);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed.'));
    xhr.send(formData);
  });

const ImageIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const ReviewHeroUpload = ({ hero = EMPTY_HERO, onHeroChange, role, label, helper, uploadedNote }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  // Track the latest upload so a Replace/Remove mid-flight discards the result.
  const uploadSeq = useRef(0);
  const lastObjectUrl = useRef(null);

  useEffect(() => () => {
    if (lastObjectUrl.current) URL.revokeObjectURL(lastObjectUrl.current);
  }, []);

  const startUpload = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      onHeroChange({ ...EMPTY_HERO, error: validationError });
      return;
    }

    const seq = ++uploadSeq.current;
    if (lastObjectUrl.current) URL.revokeObjectURL(lastObjectUrl.current);
    // HEIC object URLs can't render in <img>, so skip the instant preview there.
    const localUrl = isHeicFile(file) ? null : URL.createObjectURL(file);
    lastObjectUrl.current = localUrl;

    onHeroChange({ url: null, localUrl, uploading: true, progress: 0, error: null });

    try {
      const resized = await downscaleImage(file);
      const signature = await requestSignature();
      const uploaded = await uploadToCloudinary(resized, signature, (progress) => {
        if (uploadSeq.current === seq) {
          onHeroChange({ url: null, localUrl, uploading: true, progress, error: null });
        }
      });
      if (uploadSeq.current !== seq) return;
      // Prefer the eager derivative (downscaled JPG) — it renders everywhere,
      // including when the original was HEIC.
      const eager = Array.isArray(uploaded.eager) ? uploaded.eager : [];
      const url = eager[0]?.secure_url || uploaded.secure_url;
      onHeroChange({ url, localUrl, uploading: false, progress: 100, error: null });
    } catch (err) {
      if (uploadSeq.current !== seq) return;
      console.error('Review hero upload failed:', err);
      if (lastObjectUrl.current) {
        URL.revokeObjectURL(lastObjectUrl.current);
        lastObjectUrl.current = null;
      }
      onHeroChange({
        ...EMPTY_HERO,
        error: 'Photo upload failed — you can still submit your review and add a photo later.',
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) startUpload(file);
  };

  const handleRemove = () => {
    uploadSeq.current += 1;
    if (lastObjectUrl.current) {
      URL.revokeObjectURL(lastObjectUrl.current);
      lastObjectUrl.current = null;
    }
    onHeroChange({ ...EMPTY_HERO });
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) startUpload(file);
  };

  const previewSrc = hero.localUrl || hero.url;
  const hasPhoto = !!previewSrc;

  const helperText = helper || (role === 'host'
    ? 'A shot of the property from the shoot. This becomes the background of your shareable card.'
    : role === 'creator'
      ? 'Your best shot from the stay. This becomes the background of your shareable card.'
      : 'Hosts: a shot of the property from the shoot. Creators: your best shot from the stay. This becomes the background of your shareable card.');
  const labelText = label || 'Add a photo from your collab';
  const uploadedText = uploadedNote || 'This photo will be the background of your shareable card.';

  return (
    <div className={styles.wrapper}>
      {!hasPhoto ? (
        <button
          type="button"
          className={cn(styles.dropzone, { [styles.dragOver]: dragOver })}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <span className={styles.dropzoneIcon}><ImageIcon /></span>
          <span className={styles.dropzoneLabel}>{labelText}</span>
          <span className={styles.dropzoneHelper}>{helperText}</span>
          <span className={styles.dropzoneFormats}>JPG, PNG, or HEIC up to 15MB</span>
        </button>
      ) : (
        <div className={styles.thumbRow}>
          <div className={styles.thumbBox}>
            {previewSrc ? (
              <img src={previewSrc} alt="Collab" className={styles.thumbImg} />
            ) : (
              <span className={styles.thumbPlaceholder}><ImageIcon /></span>
            )}
            {hero.uploading && (
              <div className={styles.progressOverlay}>
                <span className={styles.spinner} aria-hidden />
                <span className={styles.progressLabel}>
                  {hero.progress > 0 ? `${hero.progress}%` : 'Uploading...'}
                </span>
              </div>
            )}
          </div>
          <div className={styles.thumbActions}>
            {hero.uploading ? (
              <span className={styles.uploadingNote}>
                Uploading your photo... you can keep writing, or even submit — the photo is optional.
              </span>
            ) : (
              <span className={styles.uploadedNote}>{uploadedText}</span>
            )}
            <div className={styles.thumbButtons}>
              <button type="button" className={styles.actionBtn} onClick={() => fileInputRef.current?.click()}>
                Replace
              </button>
              <button type="button" className={cn(styles.actionBtn, styles.removeBtn)} onClick={handleRemove}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {hero.error && <div className={styles.error}>{hero.error}</div>}

      <input
        ref={fileInputRef}
        className={styles.input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ReviewHeroUpload;
